<template>
  <div>
    <v-autocomplete
      ref="driverAutocomplete"
      :key="autocompleteKey"
      v-model="selectedDriver"
      :items="driverState.list"
      :loading="driverState.loading"
      :search-input.sync="driverState.search"
      :disabled="disabled"
      :rules="rules"
      :error-messages="errorMessages"
      :error="error"
      :label="label"
      :hint="hint"
      :persistent-hint="persistentHint"
      item-text="name"
      item-value="id"
      return-object
      prepend-inner-icon="mdi-account"
      outlined
      dense
      clearable
      :filter="filterDrivers"
      :no-data-text="''"
    >
      <template v-slot:selection="{ item }">
        <div class="d-flex align-center">
          <v-avatar size="24" class="mr-2">
            <v-icon small color="primary">mdi-account</v-icon>
          </v-avatar>
          {{ displayText(item) }}
        </div>
      </template>

      <template v-slot:item="{ item }">
        <div class="d-flex align-center py-2">
          <v-avatar size="32" class="mr-3">
            <v-icon color="primary">mdi-account</v-icon>
          </v-avatar>
          <div>
            <div class="font-weight-medium">{{ item.name }} {{ item.lastname }}</div>
            <div class="text-caption text--secondary">
              DNI: {{ item.document_number }}
              <span v-if="isCollaborator && item.dealer">
                • {{ typeof item.dealer === 'object' ? item.dealer.name : item.dealer }}
              </span>
            </div>
          </div>
        </div>
      </template>

      <template v-slot:no-data>
        <div class="px-4 py-3 text-center">
          <div v-if="!driverState.search || driverState.search.length < 3" class="text-caption grey--text">
            Escribe al menos 3 caracteres para buscar...
          </div>
          <div v-else>
            <div class="text-subtitle-2 grey--text mb-2">
              No se encontraron conductores con "{{ driverState.search }}"
            </div>
            <v-btn
              v-if="showAddDriverButton"
              small
              color="primary"
              outlined
              @click="openCreateDriverDialog"
              :disabled="createDriverDialog"
            >
              <v-icon left small>mdi-plus</v-icon>
              Crear nuevo conductor
            </v-btn>
          </div>
        </div>
      </template>
    </v-autocomplete>

    <!-- Diálogo para crear un nuevo conductor (movido fuera del autocomplete) -->
    <v-dialog 
      v-model="createDriverDialog" 
      max-width="500" 
      persistent
      :key="`driver-dialog-${autocompleteKey}`"
    >
      <driver-form 
        @save="handleDriverCreated" 
        @cancel="handleDriverDialogClose" 
        :created="true"
        :current-dealer-id="currentDealerId"
      />
    </v-dialog>
  </div>
</template>

<script>
import _ from 'lodash'
import DriverService from '../services/DriverService'
import DealerService from '../services/DealerService'
import DriverForm from '../views/driver/DriverForm'
import { mapGetters } from 'vuex'

export default {
  name: 'DriverSelector',
  components: {
    DriverForm
  },
  props: {
    value: {
      type: Object,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    first: {
      type: Boolean,
      default: false
    },
    rules: {
      type: Array,
      default: () => []
    },
    errorMessages: {
      type: Array,
      default: () => []
    },
    error: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      default: '¿QUIÉN ES EL CONDUCTOR?'
    },
    hint: {
      type: String,
      default: ''
    },
    persistentHint: {
      type: Boolean,
      default: false
    },
    currentDealerId: {
      type: [String, Number],
      default: null
    },
    transportMethod: {
      type: String,
      default: ''
    },
    lastMovement: {
      type: Object,
      default: null
    },
    currentUser: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      selectedDriver: null,
      driverState: {
        list: [],
        loading: false,
        search: ''
      },
      createDriverDialog: false,
      showNoDriversMessage: false,
      userHasDriver: false,
      checkingUserDriver: false,
      autocompleteKey: 0,
      accessibleDealers: [],
      loadingAccessibleDealers: false
    }
  },
  computed: {
    ...mapGetters({
      storeCurrentUser: 'CURRENT_USER',
      HAS_PERMISSION: 'HAS_PERMISSION'
    }),
    isCollaborator() {
      return this.$store.getters.HAS_ROLE(['colaborator'])
    },
    currentUserDealerId() {
      // Obtener el dealer_id del usuario actual
      if (this.storeCurrentUser && this.storeCurrentUser.dealer) {
        return this.storeCurrentUser.dealer.id
      }
      return null
    },
    showAddDriverButton() {
      // Verificar permisos primero
      if (!this.HAS_PERMISSION('crear_conductor')) {
        return false
      }
      
      // No mostrar si no hay búsqueda o la búsqueda es muy corta
      if (!this.driverState.search || this.driverState.search.length < 3) {
        return false
      }
      
      // No mostrar si el diálogo está abierto
      if (this.createDriverDialog) {
        return false
      }
      
      // No mostrar mientras se está cargando
      if (this.driverState.loading) {
        return false
      }
      
      // Caso especial para "YO MISMO" con movimiento reportado
      if (this.lastMovement && this.lastMovement.status === 'reported' && this.transportMethod === 'YO MISMO') {
        if (this.checkingUserDriver) {
          return false
        }
        return !this.userHasDriver
      }
      
      // Para otros casos: mostrar el botón cuando no se encontraron resultados
      // El slot no-data solo se muestra cuando no hay resultados, así que siempre mostrar el botón aquí
      return true
    },
    shouldShowNoData() {
      return this.driverState.search && this.driverState.search.length > 0 && !this.driverState.loading
    }
  },
  watch: {
    value: {
      handler(newValue) {
        this.selectedDriver = newValue
      },
      immediate: true
    },
    selectedDriver(newValue, oldValue) {
      this.$emit('input', newValue)
      this.$emit('change', newValue)
    },
    'driverState.search'(val) {
      // Solo ejecutar la búsqueda si hay un valor real (no null, no string vacío)
      if (val && val.length > 0) {
        this.driverDebouncedSearch()
      }
    },
    transportMethod(val) {
      // Si se selecciona 'YO MISMO' en un movimiento con estado 'reported', verificar driver del usuario
      if (val === 'YO MISMO' && this.lastMovement && this.lastMovement.status === 'reported') {
        this.checkUserDriver()
      }
    },
    // Watcher para createDriverDialog
    createDriverDialog(newVal, oldVal) {
      if (oldVal === true && newVal === false) {
        // Forzar re-renderizado cuando el diálogo se cierra
        this.$nextTick(() => {
          this.autocompleteKey++
        })
      }
    }
  },
  async created() {
    // Si es colaborador, cargar primero sus dealers accesibles
    if (this.isCollaborator) {
      await this.loadAccessibleDealers()
    }
    await this.loadInitialDrivers()
  },
  methods: {
    async loadInitialDrivers() {
      try {
        const driverParams = { expand: 'dealer' }
        
        // Si hay un dealer específico proporcionado, usarlo
        if (this.currentDealerId) {
          driverParams['filter[dealer_id]'] = this.currentDealerId
        } 
        
        const { data } = await DriverService.get(driverParams)
        this.driverState.list = data.data || []
        
        // Aplicar filtro por dealer del usuario si corresponde
        if ((this.isCollaborator && this.currentUserDealerId && this.first) || (this.lastMovement && this.lastMovement.status === 'reported')) {
          // Si es colaborador, usar solo el dealer del usuario actual
          // Filtrar conductores para mostrar solo los del dealer del usuario actual
          this.driverState.list = this.driverState.list.filter(driver => {
            // Posibles formas de representar el dealer en el objeto driver
            const dealerObj = driver.dealer ? driver.dealer.id : null

            // Si no hay información clara del dealer, excluir el registro
            return dealerObj && String(dealerObj) === String(this.currentUserDealerId)
          })
        }
        
        // Si hay un selectedDriver y no está en la lista, agregarlo al principio
        if (this.selectedDriver && !this.driverState.list.some(d => d.id === this.selectedDriver.id)) {
          this.driverState.list.unshift(this.selectedDriver)
        }
      } catch (error) {
        console.error('Error al obtener conductores:', error)
      }
    },
    
    async checkUserDriver() {
      if (!this.currentUser || !this.currentUser.email) {
        return
      }
      
      this.checkingUserDriver = true
      this.userHasDriver = false
      
      try {
        const userDriverParams = { 'filter[email]': this.currentUser.email, expand: 'dealer' }
        const response = await DriverService.get(userDriverParams)
        
        if (response.data && response.data.data && response.data.data.length > 0) {
          this.userHasDriver = true
          this.selectedDriver = response.data.data[0]
        } else {
          this.userHasDriver = false
        }
      } catch (error) {
        console.error('Error al verificar driver del usuario:', error)
        this.userHasDriver = false
      } finally {
        this.checkingUserDriver = false
      }
    },
    
    filterDrivers(item, queryText, itemText) {
      const text = `${item.name} ${item.lastname} ${item.document_number}`.toLowerCase()
      const searchText = queryText.toLowerCase()
      return text.includes(searchText)
    },
    
    displayText(item) {
      const driverInfo = `${item.name} ${item.lastname}`
      
      let dealerInfo = ''
      if (this.isCollaborator) {
        if (item.dealer && item.dealer.name) {
          dealerInfo = ` (${item.dealer.name})`
        } else if (item.dealer && typeof item.dealer === 'string') {
          dealerInfo = ` (${item.dealer})`
        } else if (item.dealer_name) {
          dealerInfo = ` (${item.dealer_name})`
        }
      }
      
      return driverInfo + dealerInfo + '-' + item.document_number
    },
    
    driverDebouncedSearch: _.debounce(async function () {
      if (this.driverState.search && this.driverState.search.length > 2) {
        this.driverState.loading = true
        
        try {
          const driverSearchParams = { 
            'filter[document_number]': this.driverState.search,
            expand: 'dealer'
          }
          
          // Si hay un dealer específico proporcionado, usarlo
          if (this.currentDealerId) {
            driverSearchParams['filter[dealer_id]'] = this.currentDealerId
          }
          
          const { data } = await DriverService.get(driverSearchParams)
          this.driverState.list = (data && data.data) ? data.data : []
          
          // Aplicar el mismo filtro que en loadInitialDrivers
          if ((this.isCollaborator && this.currentUserDealerId && this.first) || (this.lastMovement && this.lastMovement.status === 'reported')) {
            // Si es colaborador, usar solo el dealer del usuario actual
            // Filtrar conductores para mostrar solo los del dealer del usuario actual
            this.driverState.list = this.driverState.list.filter(driver => {
              // Posibles formas de representar el dealer en el objeto driver
              const dealerObj = driver.dealer ? driver.dealer.id : null

              // Si no hay información clara del dealer, excluir el registro
              return dealerObj && String(dealerObj) === String(this.currentUserDealerId)
            })
          }
        } catch (error) {
          console.error('Error al buscar conductores:', error)
          this.showNoDriversMessage = true
        } finally {
          this.driverState.loading = false
        }
      } else {
        this.driverState.list = []
        this.showNoDriversMessage = false
      }
      
      // Mostrar mensaje "no data" si no hay resultados y se ha buscado algo
      this.showNoDriversMessage = this.driverState.list.length === 0 && this.driverState.search && this.driverState.search.length > 2
    }, 300),
    
    openCreateDriverDialog() {
      // Resetear estados antes de abrir el diálogo
      this.showNoDriversMessage = false
      
      // Usar $nextTick para asegurar que el DOM se actualice antes de abrir el diálogo
      this.$nextTick(() => {
        this.createDriverDialog = true
      })
    },
    
    handleDriverCreated(event) {
      this.showNoDriversMessage = false
      this.createDriverDialog = false
      const newDriver = event.value.data
      
      // Agregar el nuevo driver a la lista si no existe
      const existingDriverIndex = this.driverState.list.findIndex(d => d.id === newDriver.id)
      let driverToSelect
      if (existingDriverIndex === -1) {
        this.driverState.list.unshift(newDriver) // Agregar al principio
        driverToSelect = this.driverState.list[0] // Usar la referencia de la lista
      } else {
        this.driverState.list[existingDriverIndex] = newDriver // Actualizar si existe
        driverToSelect = this.driverState.list[existingDriverIndex] // Usar la referencia de la lista
      }
      
      // Seleccionar el driver inmediatamente
      this.selectedDriver = driverToSelect
      
      // Si el nuevo driver tiene el mismo email que el usuario autenticado, actualizar userHasDriver
      if (this.currentUser && this.currentUser.email && newDriver.email === this.currentUser.email) {
        this.userHasDriver = true
      }
      
      // Emitir eventos
      this.$emit('driver-created', newDriver)
      
      // Limpiar la búsqueda después de un pequeño delay para no interferir con la selección
      setTimeout(() => {
        this.driverState.search = ''
      }, 500)
    },
    
    // Método para cuando se cancela/cierra el diálogo
    handleDriverDialogClose() {
      this.createDriverDialog = false
      this.showNoDriversMessage = false
      
      // Forzar actualización inmediata del autocomplete
      this.autocompleteKey++
      
      // Usar setTimeout para asegurar que el DOM se actualice completamente
      setTimeout(() => {
        // Segunda actualización para forzar re-renderizado del slot no-data
        this.autocompleteKey++
        
        // Forzar re-evaluación de la búsqueda si hay texto
        if (this.driverState.search && this.driverState.search.length >= 3) {
          this.driverDebouncedSearch()
        }
      }, 50)
    },
    
    // Cargar los dealers a los que el colaborador tiene acceso
    async loadAccessibleDealers() {
      if (!this.isCollaborator) {
        return
      }
      
      try {
        this.loadingAccessibleDealers = true
        const response = await DealerService.getMyAccessibleDealers()
        
        // El endpoint devuelve diferentes formatos según el caso
        let dealersArray = []
        if (response.data) {
          if (Array.isArray(response.data.data)) {
            dealersArray = response.data.data
          } else if (Array.isArray(response.data)) {
            dealersArray = response.data
          }
        }
        
        this.accessibleDealers = dealersArray
      } catch (error) {
        console.error('Error al cargar dealers accesibles:', error)
        this.accessibleDealers = []
      } finally {
        this.loadingAccessibleDealers = false
      }
    }
  }
}
</script>
